import React, {useEffect} from 'react';
import {
  Box,
  Chip,
  Divider,
  FormControlLabel,
  InputAdornment,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import {useSubscriptionConfig} from 'context/SubscriptionConfigContext';
import {useSubscriptionForm} from 'hooks/useSubscriptionForm';
import {shouldShowSection} from 'utils/subscriptionRulesUtils';
import {SubscriptionTypes} from 'constants/subscriptionTypes';

const NumberField = ({label, value, onChange, unit, max}) => (
  <TextField
    label={label}
    type="number"
    size="small"
    value={value}
    onChange={e => {
      const parsed = Math.max(0, parseInt(e.target.value) || 0);
      onChange(max !== undefined ? Math.min(parsed, max) : parsed);
    }}
    inputProps={{min: 0, ...(max !== undefined && {max})}}
    InputProps={{
      endAdornment: <InputAdornment position="end">{unit}</InputAdornment>,
    }}
    sx={{width: 150}}
  />
);

// One policy = one row: title (+ optional switch) on the left, its fields inline on the right.
const PolicyRow = ({title, hint, toggle, children}) => (
  <Box
    sx={{
      display: 'flex',
      flexWrap: 'wrap',
      alignItems: 'center',
      gap: 2,
      py: 2,
    }}>
    <Box sx={{flex: '1 1 220px', minWidth: 0}}>
      {toggle ? (
        <FormControlLabel
          sx={{m: 0, gap: 1}}
          control={
            <Switch
              size="small"
              checked={toggle.checked}
              onChange={e => toggle.onChange(e.target.checked)}
            />
          }
          label={
            <Typography variant="body2" sx={{fontWeight: 600}}>
              {title}
            </Typography>
          }
        />
      ) : (
        <Typography variant="body2" sx={{fontWeight: 600}}>
          {title}
        </Typography>
      )}
      {hint && (
        <Typography variant="caption" color="textSecondary" display="block">
          {hint}
        </Typography>
      )}
    </Box>
    <Box sx={{display: 'flex', flexWrap: 'wrap', gap: 2, flex: '2 1 320px'}}>
      {children}
    </Box>
  </Box>
);

// Compact version of RegulatoryPoliciesSection - same config paths and rules.
const CompactPoliciesSection = ({subscriptionType}) => {
  const {config, updateNestedConfig} = useSubscriptionConfig();
  const {setValue, trigger} = useSubscriptionForm();

  const showCoolingOff = shouldShowSection(subscriptionType, 'showCoolingOffPeriod');
  const showRenewal = shouldShowSection(subscriptionType, 'showRenewalSettings');
  const showCancellation = shouldShowSection(subscriptionType, 'showCancellationPolicy');
  const showBilling = shouldShowSection(subscriptionType, 'showBillingConfiguration');
  const showProRata = shouldShowSection(subscriptionType, 'showProRataRefund');
  const showTrial = shouldShowSection(subscriptionType, 'showTrialPeriod');
  const showCycles = shouldShowSection(subscriptionType, 'showBillingCycles');

  const isOneTime = subscriptionType === SubscriptionTypes.ONE_TIME;
  const noCancellation =
    subscriptionType === SubscriptionTypes.SUBSCRIPTION_NO_CANCELLATION;
  const showBillingRow =
    showBilling && (showProRata || (!isOneTime && (showCycles || showTrial)));

  const update = async (path, value) => {
    updateNestedConfig(path, value);
    setValue(`config.${path}`, value);
    await trigger(`config.${path}`);
  };

  // Cancellation is allowed for every subscription type except SUBSCRIPTION_NO_CANCELLATION
  useEffect(() => {
    const shouldAllow = !noCancellation;
    if (config.cancellation.allowed !== shouldAllow) {
      update('cancellation.allowed', shouldAllow);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subscriptionType]);

  const rows = [];

  if (showCoolingOff) {
    rows.push(
      <PolicyRow
        key="cooling"
        title="Cooling-off period"
        hint="Customers can get a refund shortly after purchase"
        toggle={{
          checked: config.coolingOffPeriod.enabled,
          onChange: v => update('coolingOffPeriod.enabled', v),
        }}>
        {config.coolingOffPeriod.enabled && (
          <>
            <NumberField
              label="Days"
              unit="days"
              value={config.coolingOffPeriod.days}
              onChange={v => update('coolingOffPeriod.days', v)}
            />
            <NumberField
              label="Refund"
              unit="%"
              max={100}
              value={config.coolingOffPeriod.refundPercentage}
              onChange={v => update('coolingOffPeriod.refundPercentage', v)}
            />
          </>
        )}
      </PolicyRow>,
    );
  }

  if (showRenewal) {
    rows.push(
      <PolicyRow
        key="renewal"
        title="Auto-renewal"
        hint="Renew automatically when the term ends"
        toggle={{
          checked: config.renewal.autoRenew,
          onChange: v => update('renewal.autoRenew', v),
        }}>
        {config.renewal.autoRenew && (
          <>
            <NumberField
              label="Grace period"
              unit="days"
              value={config.renewal.gracePeriodDays}
              onChange={v => update('renewal.gracePeriodDays', v)}
            />
            <NumberField
              label="Reminder before"
              unit="days"
              value={config.renewal.reminderDaysBefore}
              onChange={v => update('renewal.reminderDaysBefore', v)}
            />
          </>
        )}
      </PolicyRow>,
    );
  }

  if (showCancellation) {
    rows.push(
      <PolicyRow
        key="cancellation"
        title="Cancellation"
        hint={noCancellation ? null : 'Window in which customers can cancel'}>
        {noCancellation ? (
          <Chip
            size="small"
            color="error"
            label="Not allowed - customers complete the full term"
          />
        ) : (
          <>
            <NumberField
              label="Window"
              unit="days"
              value={config.cancellation.allowedWithinDays}
              onChange={v => update('cancellation.allowedWithinDays', v)}
            />
            <NumberField
              label="Refund"
              unit="%"
              max={100}
              value={config.cancellation.refundPercentage}
              onChange={v => update('cancellation.refundPercentage', v)}
            />
          </>
        )}
      </PolicyRow>,
    );
  }

  if (showBillingRow) {
    rows.push(
      <PolicyRow
        key="billing"
        title="Billing"
        toggle={
          showProRata
            ? {
                checked: config.billing.proRataRefund,
                onChange: v => update('billing.proRataRefund', v),
              }
            : undefined
        }
        hint={showProRata ? 'Pro-rata refund for the unused period' : null}>
        {!isOneTime && showCycles && (
          <NumberField
            label="Billing cycles"
            unit="cycles"
            value={config.billing.billingCycles || 0}
            onChange={v => update('billing.billingCycles', v)}
          />
        )}
        {!isOneTime && showTrial && (
          <NumberField
            label="Free trial"
            unit="days"
            value={config.billing.trialPeriodDays || 0}
            onChange={v => update('billing.trialPeriodDays', v)}
          />
        )}
      </PolicyRow>,
    );
  }

  return (
    <Box>
      {rows.map((row, index) => (
        <React.Fragment key={row.key}>
          {index > 0 && <Divider />}
          {row}
        </React.Fragment>
      ))}
    </Box>
  );
};

export default CompactPoliciesSection;
